import SidebarItem from "./SidebarItem";
import { Link, useLocation } from "react-router";
import { useState, useEffect } from "react";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";

export default function Sidebar({ sidebarLinks, sidebarFooter, children }) {
  const location = useLocation();
  const [isFixed, setIsFixed] = useState(false);
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);

  // Automatically collapse on mobile when navigating
  useEffect(() => {
    setIsMobileExpanded(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsFixed(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const CollapsedSidebarItem = ({ link }) => {
    const isActive = location.pathname.startsWith(link.href);

    return (
      <Link 
        to={link.href}
        title={link.title}
        className="tooltip tooltip-right"
        data-tip={link.title}
        data-tour={link.tourId}
      >
        <button 
          className={`p-3 rounded-lg transition-colors flex items-center justify-center ${
            isActive 
              ? "bg-blue-100 text-blue-600" 
              : "hover:bg-base-300"
          }`}
        >
          <span className={`${isActive ? "text-blue-600" : "opacity-66"}`}>
            {link.icon}
          </span>
        </button>
      </Link>
    );
  };

  return (
    <div>
      {/* Drawer */}
      <div className="drawer lg:drawer-open bg-base-100">
        <input className="drawer-toggle" id="admin-drawer" type="checkbox" />

        <div className="drawer-content flex flex-col ml-16 lg:ml-0">
          {/* Page content here */}
          <div className="p-4">
            {/* Mobile Drawer Trigger */}
            <div className="user-layout-width lg:mt-7 flex flex-col p-0 md:px-4">

              {children}
            </div>
          </div>
        </div>

        {/* Backdrop for expanded mobile sidebar */}
        {isMobileExpanded && (
          <div 
            className="fixed inset-0 bg-black/40 z-40 lg:hidden backdrop-blur-xs transition-opacity"
            onClick={() => setIsMobileExpanded(false)}
            aria-label="Close menu overlay"
          />
        )}

        {/* Collapsible sidebar on mobile, full sidebar on desktop */}
        <div 
          className={`${isFixed ? "fixed" : "absolute"} left-0 top-0 bottom-0 flex lg:hidden flex-col border-r shadow-md bg-base-100 h-screen ${
            isMobileExpanded ? "w-64 z-50 overflow-y-auto" : "w-16 z-30"
          } p-2 transition-all ease-in-out duration-300`}
        >
          {/* Mobile expand / collapse header toggle */}
          <div className="flex items-center justify-between pb-2 mb-2 border-b border-base-200">
            {isMobileExpanded ? (
              <>
                <span className="text-xs font-bold uppercase tracking-wider text-base-content/60 px-2">Navigation</span>
                <button
                  type="button"
                  onClick={() => setIsMobileExpanded(false)}
                  className="btn btn-ghost btn-xs btn-circle hover:bg-base-200 text-base-content/70"
                  aria-label="Collapse sidebar"
                  title="Collapse sidebar"
                >
                  <PanelLeftClose className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setIsMobileExpanded(true)}
                className="btn btn-ghost btn-sm w-full p-1 flex items-center justify-center hover:bg-base-200 rounded-lg text-primary"
                aria-label="Expand sidebar to view titles"
                title="Expand sidebar to view titles"
              >
                <PanelLeftOpen className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Render based on expanded/collapsed state */}
          {!isMobileExpanded ? (
            /* Icon-only view when collapsed */
            <ul className="flex flex-col gap-2">
              {sidebarLinks.map((link) =>
                link.subLinks ? (
                  <li key={link.title} className="dropdown dropdown-right">
                    <button
                      tabIndex={0}
                      title={link.title}
                      className="tooltip tooltip-right p-3 rounded-lg transition-colors flex items-center justify-center hover:bg-base-300 w-full"
                      data-tip={link.title}
                    >
                      <span className="opacity-66">
                        {link.icon}
                      </span>
                    </button>
                    <ul 
                      tabIndex={0}
                      className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-52 right-0"
                    >
                      {link.subLinks.map((subLink) => (
                        <li key={subLink.title}>
                          <Link to={subLink.href}>{subLink.title}</Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                ) : (
                  <li key={link.title}>
                    <CollapsedSidebarItem link={link} />
                  </li>
                )
              )}
            </ul>
          ) : (
            /* Full view with titles when expanded */
            <ul className="space-y-1 grow menu p-0">
              {sidebarLinks.map((link, index) =>
                link.isSection ? (
                  <li key={`section-mobile-${index}`} className="menu-title mt-4 text-xs font-semibold text-base-content/50 uppercase tracking-wider px-2">
                    {link.title}
                  </li>
                ) : link.subLinks ? (
                  <li key={link.title}>
                    <details open>
                      <summary className="flex gap-4 px-4 py-3 items-center cursor-pointer collapse-arrow after:ml-auto">
                        {link.icon}
                        <span>{link.title}</span>
                      </summary>
                      <ul className="pl-4 space-y-2">
                        {link.subLinks.map((subLink) => (
                          <SidebarItem
                            key={subLink.title}
                            link={subLink}
                            onClick={() => setIsMobileExpanded(false)}
                          />
                        ))}
                      </ul>
                    </details>
                  </li>
                ) : (
                  <SidebarItem
                    key={link.title}
                    link={link}
                    onClick={() => setIsMobileExpanded(false)}
                  />
                )
              )}
            </ul>
          )}
          {isMobileExpanded && sidebarFooter}
        </div>

        <div className="drawer-side lg:p-4 md:pe-0 border border-l shadow-xl hidden lg:block">
          <label className="drawer-overlay" htmlFor="admin-drawer"></label>

          {/* actual sidebar content */}
          <div className="menu p-4 w-60 max-w-full min-h-full bg-base-100 text-base-content">
            {/* Sidebar content */}
            <div className="flex flex-col ">
              {/* Navigation Links */}
              <ul className="space-y-1 grow">
                {sidebarLinks.map((link, index) =>
                  link.isSection ? (
                    <li key={`section-${index}`} className="menu-title mt-4 text-xs font-semibold text-base-content/50 uppercase tracking-wider">
                      {link.title}
                    </li>
                  ) : link.subLinks ? (
                    <li key={link.title}>
                      <details>
                        <summary className="flex gap-4 px-4 py-3 items-center cursor-pointer collapse-arrow after:ml-auto">
                          {link.icon}
                          <span>{link.title}</span>
                        </summary>
                        <ul className="pl-4 space-y-2">
                          {link.subLinks.map((subLink) => (
                            <SidebarItem key={subLink.title} link={subLink} />
                          ))}
                        </ul>
                      </details>
                    </li>
                  ) : (
                    <SidebarItem key={link.title} link={link} />
                  ),
                )}
              </ul>
              {sidebarFooter}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
